import { Search, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MultiSelectFilter } from '@/components/ui/multi-select-filter';
import { Select } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { ONLINE_EVAL_ISSUES_SEARCH_DEFAULTS } from '@/lib/route-search';

import type { FlaggedAgent } from '../agent-category-charts';
import type {
  IssueCategory,
  OnlineEvalEvaluation,
  OnlineEvalIssueFilters,
} from '../online-eval.types';

const DEFAULT_FILTERS: OnlineEvalIssueFilters = {
  page: 1,
  categoryIds: [],
  user: '',
  agents: [],
  models: [],
  createdAfter: ONLINE_EVAL_ISSUES_SEARCH_DEFAULTS.createdAfter,
  createdBefore: '',
  search: '',
  minConfidence: '',
  evaluator: '',
  issueCategoriesVersionId: '',
};

const evaluationKey = (evaluator: string, versionId: string | number) =>
  evaluator && versionId !== '' ? `${evaluator}|${versionId}` : '';

type IssueFiltersProps = {
  filters: OnlineEvalIssueFilters;
  categories: IssueCategory[];
  // Most recently evaluated first
  evaluations: OnlineEvalEvaluation[];
  agents: FlaggedAgent[];
  models: string[];
  onChange: (filters: OnlineEvalIssueFilters) => void;
  // Shown at the foot of the panel, opposite the reset button
  actions: ReactNode;
};

const toOptions = (values: string[]) =>
  values.map((value) => ({ value, label: value }));

// A datetime-local input has no visible way to empty itself, and empty means all time here
const DateFilter = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) => (
  <div className="relative">
    <Input
      aria-label={label}
      type="datetime-local"
      className={value ? 'pr-9' : undefined}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
    {value && (
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="absolute right-1 top-1 h-8 w-8"
        aria-label={`Clear ${label.toLowerCase()}, all time`}
        title="Clear, all time"
        onClick={() => onChange('')}
      >
        <X className="h-4 w-4" />
      </Button>
    )}
  </div>
);

// Zero means no minimum. The filters change only on release, so a drag runs one query
const ConfidenceFilter = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) => {
  const [dragged, setDragged] = useState<number | null>(null);
  const shown = dragged ?? (Number(value) || 0);

  return (
    <div className="flex h-10 items-center gap-3 rounded-md border border-input bg-background px-3">
      <span className="shrink-0 text-sm text-muted-foreground">
        Min confidence
      </span>
      <Slider
        aria-label="Minimum confidence"
        min={0}
        max={1}
        step={0.05}
        value={[shown]}
        onValueChange={([next]) => setDragged(next)}
        onValueCommit={([next]) => {
          setDragged(null);
          onChange(next === 0 ? '' : String(next));
        }}
      />
      <span className="w-10 shrink-0 text-right text-sm tabular-nums">
        {shown === 0 ? 'Any' : shown.toFixed(2)}
      </span>
    </div>
  );
};

export const IssueFilters = ({
  filters,
  categories,
  evaluations,
  agents,
  models,
  onChange,
  actions,
}: IssueFiltersProps) => {
  const update = (change: Partial<OnlineEvalIssueFilters>) =>
    onChange({ ...filters, ...change, page: 1 });
  const hasFilters = (
    Object.keys(DEFAULT_FILTERS) as (keyof OnlineEvalIssueFilters)[]
  ).some((key) => {
    const value = filters[key];
    return (
      key !== 'page' &&
      (Array.isArray(value) ? value.length > 0 : value !== DEFAULT_FILTERS[key])
    );
  });
  const categoryOptions = categories.map((category) => ({
    value: category.id,
    label: category.deprecated
      ? `${category.name} (deprecated)`
      : category.name,
  }));
  // Selections come from the filters rather than from the options, so one with no
  // option, such as an agent whose findings were all deleted, stays removable
  const selectedCategoryOptions = filters.categoryIds.map(
    (categoryId) =>
      categoryOptions.find((option) => option.value === categoryId) ?? {
        value: categoryId,
        label: categoryId,
      }
  );

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <span className="text-xs font-semibold uppercase text-muted-foreground">
          Filters
        </span>
        <span className="text-xs text-muted-foreground">
          Apply to the findings table and the agent comparison charts, which
          show one evaluation at a time: one evaluator given one version of the
          categories. Leave a date empty for all time. A minimum confidence
          leaves out findings without a confidence.
        </span>
      </div>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <Select
          aria-label="Evaluation"
          className="md:col-span-2"
          value={evaluationKey(
            filters.evaluator,
            filters.issueCategoriesVersionId
          )}
          onChange={(event) => {
            const [evaluator = '', issueCategoriesVersionId = ''] =
              event.target.value.split('|');
            update({ evaluator, issueCategoriesVersionId });
          }}
        >
          <option value="">Most recent evaluation</option>
          {evaluations.map((evaluation) => (
            <option
              key={evaluationKey(
                evaluation.evaluator,
                evaluation.issue_categories_version_id
              )}
              value={evaluationKey(
                evaluation.evaluator,
                evaluation.issue_categories_version_id
              )}
            >
              {evaluation.evaluator}, categories version{' '}
              {evaluation.issue_categories_version_id} (last evaluated{' '}
              {new Date(evaluation.last_evaluated_at).toLocaleDateString()})
            </option>
          ))}
        </Select>
        <label className="relative md:col-span-2">
          <span className="sr-only">Search findings</span>
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search IDs or evaluator explanations"
            value={filters.search}
            onChange={(event) => update({ search: event.target.value })}
          />
        </label>
        <MultiSelectFilter
          ariaLabel="Issue categories"
          placeholder="All categories"
          options={categoryOptions}
          value={selectedCategoryOptions}
          onChange={(selected) =>
            update({ categoryIds: selected.map((option) => option.value) })
          }
        />
        <Input
          aria-label="User email or ID"
          placeholder="User email or ID"
          value={filters.user}
          onChange={(event) => update({ user: event.target.value })}
        />
        <MultiSelectFilter
          ariaLabel="Agents"
          placeholder="All agents"
          options={agents.map(({ agent, chatCount }) => ({
            value: agent,
            label:
              chatCount === null
                ? agent
                : `${agent} (${chatCount.toLocaleString()} chats)`,
          }))}
          value={toOptions(filters.agents)}
          onChange={(selected) =>
            update({ agents: selected.map((option) => option.value) })
          }
        />
        <MultiSelectFilter
          ariaLabel="Models"
          placeholder="All models"
          options={toOptions(models)}
          value={toOptions(filters.models)}
          onChange={(selected) =>
            update({ models: selected.map((option) => option.value) })
          }
        />
        <DateFilter
          label="Findings after"
          value={filters.createdAfter}
          onChange={(createdAfter) => update({ createdAfter })}
        />
        <DateFilter
          label="Findings before"
          value={filters.createdBefore}
          onChange={(createdBefore) => update({ createdBefore })}
        />
        <ConfidenceFilter
          value={filters.minConfidence}
          onChange={(minConfidence) => update({ minConfidence })}
        />
      </div>
      <div className="mt-3 flex flex-wrap items-start gap-2">
        {hasFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onChange(DEFAULT_FILTERS)}
          >
            <X className="mr-1 h-4 w-4" /> Reset filters
          </Button>
        )}
        <div className="ml-auto">{actions}</div>
      </div>
    </div>
  );
};
